import { headerAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { domainToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

const DEFAULT_CONSOLE_URL = "https://console.xbow.com";

const consoleUrl = (configuration: Record<string, string>) =>
  configuration.instanceUrl?.trim().replace(/\/+$/, "") || DEFAULT_CONSOLE_URL;

export const xbow = defineConnector({
  identifier: "xbow",
  name: "XBOW",
  tagLine: "Automated penetration testing",
  logo,
  protocol: "http",
  tags: ["security"],
  defaultUrlPattern: (configuration) =>
    domainToRegex(consoleUrl(configuration)),
  // XBOW rejects every request that omits x-xbow-api-version, so the bearer
  // token alone returns 401/fetch errors.
  auth: (app) =>
    headerAuth([
      { name: "Authorization", value: app.secret.apiKey, scheme: "Bearer" },
      { name: "x-xbow-api-version", value: "2026-07-01" },
    ]),
  steps: [
    {
      title: "Create an API key",
      items: [
        {
          kind: "markdown",
          content:
            "In your XBOW console, go to **Settings > API Keys** and create an **organization key** scoped to the organization whose findings you want to retrieve. XBOW issues organization keys for findings, assets, and assessments; integration keys (a separate key type) are only for organization management.",
        },
      ],
    },
    {
      title: "Enter your credentials",
      items: [
        {
          kind: "url",
          label: "XBOW console URL",
          placeholder: DEFAULT_CONSOLE_URL,
          defaultValue: DEFAULT_CONSOLE_URL,
          target: { kind: "configuration", field: "instanceUrl" },
          helperText:
            "Leave the default for XBOW’s cloud, or enter your self-hosted XBOW console URL.",
        },
        {
          kind: "input",
          label: "Organization key",
          required: true,
          target: { kind: "secrets", field: "apiKey" },
        },
        {
          kind: "input",
          label: "Organization ID",
          required: true,
          target: { kind: "configuration", field: "organizationId" },
          helperText:
            "Find this in the URL on your organization page, e.g. console.xbow.com/v2/organizations/<your-org-id>.",
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: (configuration) =>
      `${consoleUrl(configuration)}/api/v1/organizations/${configuration.organizationId}`,
  },
});
